import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import Notification from '@rio-cloud/rio-uikit/Notification';
import React, { Fragment, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import ChatAssetTree from '@/components/atom/ChatAssetTree/ChatAssetTree';
import DocumentViewer from '@/components/organism/DocumentViewer/DocumentViewer';
import { getLocale } from '@/configuration/lang/langSlice';
import { getUserProfile } from '@/configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { getAccessToken } from '@/configuration/tokenHandling/tokenSlice';
import useRestoreActiveThread from '@/hooks/useRestoreActiveThread';
import { componentMap } from '@/routes/componentRoutes';
import { useSendQuestionMutation } from '@/services/chatApi/chatApi';
import { fetchProcessingStatus } from '@/services/chatApi/fetchProcessingStatus';
import { TRouteRequest } from '@/services/chatApi/types';
import { EResponseStatus } from '@/services/config/types';
import { useFetchCookiesQuery } from '@/services/cookieApi/cookieApi';
import { threadApi } from '@/services/threadApi/threadApi';
import { addMessage, addNextOnboardingQuestion, clearManyMessages } from '@/store/chat/chat.slice';
import { resetToOnboarding, selectCurrentLayout, setCurrentLayout } from '@/store/layout/layoutSlice';
import {
  activateOnboardingMode,
  handleNextOnboardingStep,
  selectIsOnboardingMode,
  selectIsOnboardingQuestionLoading,
  setOnboardingQuestionLoading,
} from '@/store/onboarding/onboardingSlice';
import { addStatus, clearStatuses, setConnectionStatus } from '@/store/processingStatus/processingStatusSlice';
import { openSidebar } from '@/store/sidebar/sidebarSlice';
import { reduxStore } from '@/store/store';
import { addNewThread, selectActiveThreadId, updateThreadTitle } from '@/store/thread/threadSlice';
import { ONBOARDING_THREAD_ID } from '@/utils/constants';
import { getUserId } from '@/utils/getUserId';
import Logger from '@/utils/logger';
import { toAvailableLanguagesMapper, toLanguageDisplayName } from '@/utils/mappers/languagesMapper';
import { createTemporaryMessageId, isTemporaryThreadId } from '@/utils/temporaryUtils/temporaryIds';

import MfaNotification from '../../molecule/MfaNotification/MfaNotification';
import OnboardingWalkthrough from '../../organism/OnboardingWalkthrough/OnboardingWalkthrough';
import { THandleSendParams } from './types';

const LOGGER_TAG = 'ChatPage';

const ChatPage = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const activePage = useAppSelector(selectCurrentLayout);
  const currentLanguage = useAppSelector(getLocale);
  const activeThreadId = useAppSelector(selectActiveThreadId);
  const isOnboardingMode = useAppSelector(selectIsOnboardingMode);
  const isOnboardingQuestionLoading = useAppSelector(selectIsOnboardingQuestionLoading);
  const user = useAppSelector(getUserProfile);
  const activeThreadIdRef = useRef(activeThreadId);
  const userId = getUserId(user?.sub ?? '');
  const [sendMessage, { isError, isLoading, error: sendMessageError }] = useSendQuestionMutation();
  const LAST_SEEN_MFA_NOTIFICATION = 'lastSeenMfaNotification';
  const DAYS_30 = 30 * 24 * 60 * 60 * 1000;
  const accessToken = useAppSelector(getAccessToken);
  const { error } = useFetchCookiesQuery(accessToken);

  const { mainComponent: MainComponent } = componentMap[activePage as keyof typeof componentMap];

  useRestoreActiveThread();

  const [deleteMessagesByThreadId] = threadApi.endpoints.deleteMessagesByThreadId.useLazyQuery();

  useEffect(() => {
    if (error) {
      Logger.error(LOGGER_TAG, 'Failed to fetch cookies', error);
    }
  }, [error]);

  const mfaNotificationSettings = {
    title: intl.formatMessage({ id: 'mfa.notification.title' }),
    timeout: 999999,
    closeButton: true,
    hideProgressBar: false,
  };

  const showMfaNotification = () => {
    Notification.info(<MfaNotification />, mfaNotificationSettings);
    localStorage.setItem(LAST_SEEN_MFA_NOTIFICATION, Date.now().toString());
  };

  const handleSend = async ({ content, isOnboarding = false, nextStepIndex }: THandleSendParams) => {
    try {
      // INFO: Reads the id, not the thread entity because after a reload the entity is only available once
      // the paginated thread list has loaded, which would otherwise spawn a duplicate thread here.
      let currentActiveThreadId = selectActiveThreadId(reduxStore.getState());

      if (isOnboarding) {
        dispatch(
          addMessage({
            messageId: '',
            content,
            role: 'human',
            threadId: ONBOARDING_THREAD_ID,
            timestamp: new Date().toISOString(),
            isOnboardingQuestion: true,
            data_visualization: {
              default_data_visualization: null,
              x_axis: null,
            },
            language: toAvailableLanguagesMapper(intl.locale),
          }),
        );
        dispatch(handleNextOnboardingStep());
        const updatedStep = reduxStore.getState().onboarding.onboardingStep;
        dispatch(setOnboardingQuestionLoading(true));
        setTimeout(
          () => {
            dispatch(
              addNextOnboardingQuestion({
                step: updatedStep ?? 'step-1',
                language: reduxStore.getState().lang.displayLocale ?? 'en-US',
              }),
            );
            dispatch(setOnboardingQuestionLoading(false));
          },
          updatedStep === 'step-4' ? 20000 : 8000,
        );
      }
      if (!isOnboarding) {
        if (!currentActiveThreadId) {
          dispatch(addNewThread());
          currentActiveThreadId = selectActiveThreadId(reduxStore.getState());
        }
        dispatch(updateThreadTitle({ title: content }));

        const optimisticMessageId = createTemporaryMessageId();
        // INFO: Sent with both the question and its progress stream, which may reach the server in either order
        const requestId = crypto.randomUUID();
        // INFO: Ends the progress stream once the answer settles. Left open, the stream library re-opens it when a
        // hidden tab is shown again, and the API replays the finished question into the next one's statuses
        const progressStream = new AbortController();

        const threadId = currentActiveThreadId ?? null;
        const requestedMessage: TRouteRequest = {
          question: content,
          language: toLanguageDisplayName(toAvailableLanguagesMapper(currentLanguage)),
          threadId,
          meta: { isOnboarding, currentLanguage, nextStepIndex },
          userId,
          optimisticMessageId,
          requestId,
        };
        dispatch(
          addMessage({
            messageId: optimisticMessageId,
            content,
            role: 'human',
            threadId,
            timestamp: new Date().toISOString(),
            isOnboardingQuestion: false,
            data_visualization: {
              default_data_visualization: null,
              x_axis: null,
            },
            language: toAvailableLanguagesMapper(currentLanguage),
          }),
        );

        if (!isOnboardingQuestionLoading) {
          fetchProcessingStatus({
            userId,
            requestId,
            signal: progressStream.signal,
            onMessage: (data) => {
              const liveActiveThreadId = selectActiveThreadId(reduxStore.getState());
              const isOptimisticThread = liveActiveThreadId !== null && isTemporaryThreadId(liveActiveThreadId);
              // INFO: Processing status is visible when SSE threadId matches the current thread, except for optimistic updates where it is always visible
              const visible =
                liveActiveThreadId === null || isOptimisticThread || liveActiveThreadId === data.thread_id;
              dispatch(
                addStatus({
                  id: data.event_id,
                  message: data.message,
                  completed: false,
                  visible,
                  threadId: data.thread_id,
                  type: data.type,
                }),
              );
            },
            onOpen: () => {
              dispatch(setConnectionStatus({ status: 'connected' }));
            },
            onError: (error) => {
              Logger.error(LOGGER_TAG, 'Connection error:', error);
              // Opt-out
            },
            onClose: () => {
              dispatch(setConnectionStatus({ status: 'disconnected' }));
            },
          });
        }
        try {
          await sendMessage(requestedMessage).unwrap();
        } finally {
          progressStream.abort();
        }
      }
    } catch (err) {
      Logger.error(LOGGER_TAG, 'Failed to send the message: ', err);
    }
    dispatch(clearStatuses());
  };

  const handleDelete = async (messageId: string) => {
    const threadId = activeThreadIdRef.current ?? '';
    try {
      const response = await deleteMessagesByThreadId({
        userId,
        threadId,
        messageId: messageId.toString(),
      }).unwrap();

      if (response.status === EResponseStatus.SUCCESS) {
        Notification.success(intl.formatMessage({ id: 'chatPage.SpeechBubble.confirmDeletion.success' }));
        dispatch(clearManyMessages({ idsToDelete: response.messageIds }));
      } else {
        Notification.error(intl.formatMessage({ id: 'chatPage.SpeechBubble.confirmDeletion.fail' }));
      }
    } catch (error) {
      Notification.error(intl.formatMessage({ id: 'common.error' }));
      Logger.error(LOGGER_TAG, 'Failed to delete the message: ', error);
    }
  };

  useEffect(() => {
    if (isError) {
      dispatch(
        addMessage({
          messageId: '',
          content: intl.formatMessage({ id: sendMessageError?.message || 'common.error' }),
          role: 'ai',
          threadId: activeThreadId ?? null,
          timestamp: new Date().toISOString(),
          isOnboardingQuestion: false,
          data_visualization: {
            default_data_visualization: null,
            x_axis: null,
          },
          language: toAvailableLanguagesMapper(currentLanguage),
        }),
      );
    }
  }, [isError]);

  useEffect(() => {
    activeThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  useEffect(() => {
    if (isOnboardingMode) {
      dispatch(openSidebar());
    }
  }, [isOnboardingMode, dispatch]);

  useEffect(() => {
    if (activePage !== 'chat') return;

    const isFirstLogin = localStorage.getItem('isFirstLoginOnboarding') === null;
    if (!isFirstLogin) return;

    dispatch(setCurrentLayout('onboardingMode'));
    dispatch(activateOnboardingMode());
    dispatch(resetToOnboarding());
    localStorage.setItem('isFirstLoginOnboarding', 'false');
    navigate('/onboardingMode', { replace: true });
  }, [activePage, dispatch, navigate]);

  useEffect(() => {
    const lastSeen = Number(localStorage.getItem(LAST_SEEN_MFA_NOTIFICATION)) || 0;
    const elapsed = Date.now() - lastSeen;

    if (elapsed > DAYS_30) {
      showMfaNotification();
    }
  }, [showMfaNotification]);

  return (
    <Fragment>
      <ChatAssetTree isLoading={isLoading} />
      {/* INFO: rio's application layout floats its sidebars, so a right sidebar has to precede the body in
          the DOM or it drops to a row of its own below the conversation in the DOM. */}
      <DocumentViewer />
      <ApplicationLayout.Body>
        {React.createElement(MainComponent, {
          handleSend,
          isLoading,
          isError,
          handleDelete,
        })}
        <OnboardingWalkthrough />
      </ApplicationLayout.Body>
    </Fragment>
  );
};

export default ChatPage;
